import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  TextInput,
  Alert,
  ActivityIndicator,
  TouchableOpacity,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { logActivity } from "@/lib/activity-log";

/**
 * Section header component
 */
interface SectionHeaderProps {
  title: string;
  subtitle?: string;
}

const SectionHeader = ({ title, subtitle }: SectionHeaderProps) => {
  const colors = useThemeColors();

  return (
    <View className="mb-4">
      <Text className="text-lg font-bold" style={{ color: colors.text }}>
        {title}
      </Text>
      {subtitle && (
        <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
          {subtitle}
        </Text>
      )}
    </View>
  );
};

/**
 * Form input with label and optional password visibility toggle
 */
interface FormInputProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  secureTextEntry?: boolean;
  keyboardType?: "default" | "email-address";
  autoCapitalize?: "none" | "sentences";
  editable?: boolean;
  showPasswordToggle?: boolean;
}

const FormInput = ({
  label,
  value,
  onChangeText,
  placeholder,
  secureTextEntry = false,
  keyboardType = "default",
  autoCapitalize = "none",
  editable = true,
  showPasswordToggle = false,
}: FormInputProps) => {
  const colors = useThemeColors();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  // Determine if text should be hidden (only if secureTextEntry is true and visibility is off)
  const shouldHideText = secureTextEntry && !isPasswordVisible;

  return (
    <View className="mb-4">
      <Text
        className="text-sm font-medium mb-2"
        style={{ color: colors.textSecondary }}
      >
        {label}
      </Text>
      <View
        className="flex-row items-center rounded-xl px-4"
        style={{
          backgroundColor: editable ? colors.surfaceSecondary : colors.backgroundTertiary,
          borderWidth: 1,
          borderColor: colors.border,
          opacity: editable ? 1 : 0.7,
        }}
      >
        <TextInput
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.textTertiary}
          secureTextEntry={shouldHideText}
          keyboardType={keyboardType}
          autoCapitalize={autoCapitalize}
          editable={editable}
          className="flex-1 py-3"
          style={{ color: colors.text }}
        />
        {showPasswordToggle && secureTextEntry && (
          <TouchableOpacity
            onPress={() => setIsPasswordVisible(!isPasswordVisible)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel={isPasswordVisible ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
          >
            <Ionicons
              name={isPasswordVisible ? "eye-outline" : "eye-off-outline"}
              size={20}
              color={colors.textTertiary}
            />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

/**
 * Account Settings Screen
 * Allows users to change email and password
 */
export default function AccountSettingsScreen() {
  const router = useRouter();
  const { profile, user, signOut } = useAuth();
  const { isDark } = useTheme();
  const colors = useThemeColors();

  // Email change state
  const [newEmail, setNewEmail] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Account deletion state
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  /**
   * Handle email change
   * Supabase sends confirmation to both old and new email
   */
  const handleChangeEmail = async () => {
    if (!newEmail.trim()) {
      Alert.alert("Fout", "Voer een nieuw e-mailadres in");
      return;
    }

    if (newEmail.toLowerCase() === profile?.email?.toLowerCase()) {
      Alert.alert("Fout", "Dit is je huidige e-mailadres");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      Alert.alert("Fout", "Voer een geldig e-mailadres in");
      return;
    }

    // Safety confirmation modal with detailed explanation
    Alert.alert(
      "Weet je zeker dat je je e-mailadres wilt wijzigen?",
      `Let op:\n\n` +
      `• Je ontvangt een bevestigingslink op je huidige e-mailadres (${profile?.email})\n\n` +
      `• Je ontvangt ook een bevestigingslink op je nieuwe e-mailadres (${newEmail})\n\n` +
      `• Je moet op de link in de email klikken, de knop met "Bevestigen" om de wijziging door te voeren\n\n` +
      `• Na het bevestigen van de link kun je alleen nog inloggen met je nieuwe e-mailadres`,
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Ja, wijzig mijn e-mail",
          style: "destructive",
          onPress: async () => {
            setIsChangingEmail(true);
            try {
              const { error } = await supabase.auth.updateUser({
                email: newEmail.trim().toLowerCase(),
              });

              if (error) throw error;

              await logActivity({
                user_id: user?.id,
                action_type: "profile.update",
                action_category: "profile",
                target_type: "user",
                target_id: user?.id,
                metadata: { change_type: "email", new_email: newEmail.trim().toLowerCase() },
              });

              Alert.alert(
                "Bevestigingslinks Verstuurd",
                `We hebben bevestigingslinks gestuurd naar:\n\n` +
                `📧 ${profile?.email}\n` +
                `📧 ${newEmail}\n\n` +
                `Open beide e-mails en klik op de bevestigingslinks om de wijziging te voltooien.\n\n` +
                `Je wordt nu uitgelogd. Na het bevestigen van beide links kun je inloggen met je nieuwe e-mailadres.`,
                [
                  {
                    text: "Begrepen",
                    onPress: async () => {
                      setNewEmail("");
                      await signOut();
                    },
                  },
                ]
              );
            } catch (error: any) {
              console.error("Error changing email:", error);
              Alert.alert("Fout", error.message || "Kon e-mail niet wijzigen");
            } finally {
              setIsChangingEmail(false);
            }
          },
        },
      ]
    );
  };

  /**
   * Handle password change
   */
  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      Alert.alert("Fout", "Vul beide wachtwoordvelden in");
      return;
    }

    if (newPassword.length < 8) {
      Alert.alert("Fout", "Wachtwoord moet minimaal 8 karakters zijn");
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert("Fout", "Wachtwoorden komen niet overeen");
      return;
    }

    setIsChangingPassword(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      await logActivity({
        user_id: user?.id,
        action_type: "profile.update",
        action_category: "profile",
        target_type: "user",
        target_id: user?.id,
        metadata: { change_type: "password" },
      });

      Alert.alert(
        "Wachtwoord Gewijzigd",
        "Je wachtwoord is succesvol gewijzigd.",
        [
          {
            text: "OK",
            onPress: () => {
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
            },
          },
        ]
      );
    } catch (error: any) {
      console.error("Error changing password:", error);
      Alert.alert("Fout", error.message || "Kon wachtwoord niet wijzigen");
    } finally {
      setIsChangingPassword(false);
    }
  };

  /**
   * Handle delete account
   * Uses soft-delete/anonymization approach
   */
  const handleDeleteAccount = () => {
    Alert.alert(
      "Account Verwijderen",
      "Weet je zeker dat je je account wilt verwijderen?\n\n" +
        "⚠️ Deze actie kan niet ongedaan worden gemaakt.\n\n" +
        "Je profiel wordt geanonimiseerd en je kunt niet meer inloggen. " +
        "Berichten in chats blijven zichtbaar als 'Verwijderd Gebruiker'.",
      [
        { text: "Annuleer", style: "cancel" },
        {
          text: "Verwijderen",
          style: "destructive",
          onPress: () => confirmDeleteAccount(),
        },
      ]
    );
  };

  /**
   * Confirm and execute account deletion
   */
  const confirmDeleteAccount = async () => {
    if (!user?.id) return;

    setIsDeletingAccount(true);

    try {
      // Call the anonymize function
      const { data, error } = await supabase.rpc("anonymize_user_account", {
        p_user_id: user.id,
        p_reason: "self_requested",
      });

      if (error) throw error;

      Alert.alert(
        "Account Verwijderd",
        "Je account is succesvol verwijderd. Je wordt nu uitgelogd.",
        [
          {
            text: "OK",
            onPress: async () => {
              await signOut();
            },
          },
        ]
      );
    } catch (error: any) {
      console.error("Error deleting account:", error);
      Alert.alert(
        "Fout",
        error.message || "Er ging iets mis bij het verwijderen van je account"
      );
    } finally {
      setIsDeletingAccount(false);
    }
  };

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top"]}
    >
      {/* Header */}
      <View
        className="flex-row items-center px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <Pressable
          onPress={() => router.back()}
          className="w-10 h-10 rounded-full items-center justify-center"
          style={{ backgroundColor: colors.surfaceSecondary }}
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Ionicons name="arrow-back" size={20} color={colors.text} />
        </Pressable>
        <Text
          className="flex-1 text-center text-lg font-semibold mr-10"
          style={{ color: colors.text }}
        >
          Account Instellingen
        </Text>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerClassName="px-4 py-6"
        showsVerticalScrollIndicator={false}
      >
        {/* Current Email Display */}
        <View
          className="rounded-xl p-4 mb-6"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          <View className="flex-row items-center">
            <View
              className="w-10 h-10 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Ionicons name="mail" size={20} color={colors.primary} />
            </View>
            <View className="flex-1">
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Huidig e-mailadres
              </Text>
              <Text className="font-medium" style={{ color: colors.text }}>
                {profile?.email}
              </Text>
            </View>
          </View>
        </View>

        {/* Change Email Section */}
        <View
          className="rounded-xl p-4 mb-6"
          style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
        >
          <SectionHeader
            title="E-mailadres Wijzigen"
            subtitle="Je ontvangt een bevestigingslink op beide adressen"
          />

          <FormInput
            label="Nieuw E-mailadres"
            value={newEmail}
            onChangeText={setNewEmail}
            placeholder="nieuw@email.nl"
            keyboardType="email-address"
          />

          <Pressable
            onPress={handleChangeEmail}
            disabled={isChangingEmail || !newEmail.trim()}
            className="rounded-xl py-3 items-center"
            style={{
              backgroundColor:
                isChangingEmail || !newEmail.trim()
                  ? colors.textTertiary
                  : colors.primary,
            }}
            accessibilityRole="button"
            accessibilityLabel="E-mailadres wijzigen"
          >
            {isChangingEmail ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold">E-mail Wijzigen</Text>
            )}
          </Pressable>
        </View>

        {/* Change Password Section */}
        <View
          className="rounded-xl p-4 mb-6"
          style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
        >
          <SectionHeader
            title="Wachtwoord Wijzigen"
            subtitle="Kies een sterk wachtwoord van minimaal 8 karakters"
          />

          <FormInput
            label="Nieuw Wachtwoord"
            value={newPassword}
            onChangeText={setNewPassword}
            placeholder="Minimaal 8 karakters"
            secureTextEntry
            showPasswordToggle
          />

          <FormInput
            label="Bevestig Wachtwoord"
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder="Herhaal wachtwoord"
            secureTextEntry
            showPasswordToggle
          />

          <Pressable
            onPress={handleChangePassword}
            disabled={isChangingPassword || !newPassword || !confirmPassword}
            className="rounded-xl py-3 items-center"
            style={{
              backgroundColor:
                isChangingPassword || !newPassword || !confirmPassword
                  ? colors.textTertiary
                  : colors.primary,
            }}
            accessibilityRole="button"
            accessibilityLabel="Wachtwoord wijzigen"
          >
            {isChangingPassword ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text className="text-white font-semibold">Wachtwoord Wijzigen</Text>
            )}
          </Pressable>
        </View>

        {/* Danger Zone */}
        <View
          className="rounded-xl p-4 mb-8"
          style={{ backgroundColor: colors.errorLight, borderWidth: 1, borderColor: colors.error }}
        >
          <SectionHeader
            title="Gevarenzone"
            subtitle="Onomkeerbare acties"
          />

          <Pressable
            onPress={handleDeleteAccount}
            disabled={isDeletingAccount}
            className="rounded-xl py-3 items-center border"
            style={{ 
              borderColor: colors.error,
              opacity: isDeletingAccount ? 0.6 : 1,
            }}
            accessibilityRole="button"
            accessibilityLabel="Account verwijderen"
          >
            {isDeletingAccount ? (
              <ActivityIndicator color={colors.error} />
            ) : (
              <Text style={{ color: colors.error }} className="font-semibold">
                Account Verwijderen
              </Text>
            )}
          </Pressable>
        </View>

        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}

